import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { JWT_REFRESH_TIMER_THRESHOLD_SECONDS } from "../../helpers/constants";
import { useLocalStorage } from "../../helpers/hooks/useLocalStorage";
import usePusher from "../../helpers/hooks/usePusher";
import { useTicker } from "../../helpers/hooks/useTicker";
import { getEnvValueFor, gatehouseProfile, isGatehouseJwt, parseJwt } from "../../helpers/utils";
import { authmsApi, rmsApi } from "../../state/axios";
import { authOperations } from "../../state/features/auth";
import AuthTokenError from "../authTokenError";
import { setIsAdminUser } from "../../state/features/company/slice";

interface IAuthProvideProps {
  children: React.ReactNode;
}

const AuthProvider = ({ children }: IAuthProvideProps) => {
  const [jwt, setJwt] = useLocalStorage("jwt", "");
  const [refreshToken] = useLocalStorage("rt", "");

  const { authorize, tokenExpiry, initiateTicker, isTokenExpired } =
    useSelector((state: any) => state.auth);
  const dispatch = useDispatch();
  const { authorizeUser, setJwtData } = authOperations;
  const { updateJwtForPusher } = usePusher();

  const decodeAuthDataFromJwt = (token: string) => {
    try {
      if (token) {
        const authData = parseJwt(token);
        if (isGatehouseJwt(token)) {
          // A Gatehouse token does not carry is_admin; the sign-in stored it next to the token.
          const profile = gatehouseProfile();
          authData.is_admin = profile.is_admin === true;
          if (profile.authorized_companies) authData.authorized_companies = profile.authorized_companies;
        }
        console.debug(
          `[AUTH] Token parse success. expiry: ${authData.exp} seconds`
        );
        console.debug(
          `[AUTH] New token will expire on ${new Date(authData.exp * 1000)}`
        );
        setJwtData(authData);
        if (authData.is_admin) {
          dispatch(setIsAdminUser(true));
        }
      }
    } catch (error) {
      console.debug("[AUTH] Invalid Token");
      console.log(error);
      authorizeUser(false);
    }
  };

  // Sessions that started through Gatehouse refresh through this app (it holds the client secret). Refresh tokens are single use: the
  // answer holds the next one. Several tabs share one session, so only one tab refreshes at a time (Web Locks), and a tab that finds a
  // fresh token already stored by another tab just uses it instead of presenting an old refresh token a second time.
  const refreshWithGatehouse = () => {
    const run = () => {
      const storedJwt = window.localStorage.getItem("jwt") || "";
      try {
        const expiry = parseJwt(storedJwt).exp;
        if (expiry - Date.now() / 1000 > JWT_REFRESH_TIMER_THRESHOLD_SECONDS + 5) {
          setJwt(storedJwt);
          return Promise.resolve();
        }
      } catch (error) {
        // fall through and refresh
      }
      return rmsApi
        .post("/auth/gatehouse/refresh", {
          refresh_token: window.localStorage.getItem("rt") || "",
        })
        .then((response) => {
          console.debug("[AUTH] Gatehouse token refresh success");
          window.localStorage.setItem("rt", response.data.rt);
          window.localStorage.setItem("gh_profile", JSON.stringify(response.data.profile || {}));
          setJwt(response.data.jwt);
          updateJwtForPusher(response.data.jwt);
        })
        .catch((error) => {
          console.debug("[AUTH] Gatehouse token refresh failed");
          console.log(error);
          authorizeUser(false);
        });
    };
    const locks = (navigator as any).locks;
    return locks ? locks.request("vantage-gatehouse-refresh", run) : run();
  };

  const handleTokenRefresh = () => {
    if (isGatehouseJwt(window.localStorage.getItem("jwt"))) {
      refreshWithGatehouse();
      return;
    }
    console.debug("[AUTH] Refreshing JWT token");

    const demoSessionRefresh = () => {
      try {
        const payload = parseJwt(jwt);
        return Boolean(payload.demo_account && payload.demo_session_id);
      } catch {
        return false;
      }
    };

    if (demoSessionRefresh()) {
      rmsApi
        .post("/api/v1/demo-sessions/refresh")
        .then((response) => {
          console.debug("[AUTH] Demo session JWT refresh success");
          setJwt(response.data.jwt);
          if (response.data.refresh_token) {
            window.localStorage.setItem("rt", response.data.refresh_token);
          }
          updateJwtForPusher(response.data.jwt);
        })
        .catch((error) => {
          console.debug("[AUTH] Demo session JWT refresh failed");
          console.log(error);
          authorizeUser(false);
        });
      return;
    }

    authmsApi
      .post(
        `/api/dashboard/v1/tokens/refresh`,
        {
          jwt: jwt,
          refresh_token: refreshToken,
        },
        {
          baseURL: `${getEnvValueFor("AUTH_MS_BASE_URL")}`,
        }
      )
      .then((response) => {
        console.debug("[AUTH] JWT token refresh success");
        setJwt(response.data.jwt);
        updateJwtForPusher(jwt);
      })
      .catch((error) => {
        console.debug("[AUTH] JWT token refresh failed");
        console.log(error);
        authorizeUser(false);
      });
  };

  const [tickerStatus, startTicker, setTokenExpiry] = useTicker({
    thresholdValue: JWT_REFRESH_TIMER_THRESHOLD_SECONDS,
    onThresholdTrigger: handleTokenRefresh,
  });

  useEffect(() => {
    if (isTokenExpired) {
      handleTokenRefresh();
    }
  }, [isTokenExpired]);

  useEffect(() => {
    if (jwt) {
      decodeAuthDataFromJwt(jwt);
    } else {
      // If jwt is missing in localStorage
      authorizeUser(false);
    }
  }, [jwt]);

  useEffect(() => {
    if (initiateTicker && tokenExpiry) {
      setTokenExpiry(tokenExpiry); // Pass expiry value to ticker
      startTicker(true);
    }
  }, [initiateTicker, tokenExpiry]);

  return authorize ? <>{children}</> : <AuthTokenError />;
};

export default AuthProvider;
